/**
 * Cliente REST del billing de academia (F3-H). Los datos financieros son
 * SERVER-ONLY (sin PowerSync en v1), así que van por el transporte compartido de
 * `@/lib/rest` —la única pieza que sabe autenticar en web (cookie-BFF) y en
 * Tauri (Bearer inyectado en Rust)— en vez de por el store local-first.
 * Envuelto en TanStack Query para caché/estados de carga.
 */
import { createMutation, createQuery, useQueryClient } from "@tanstack/solid-query";
import { queryKeys } from "@space/shared";
import { apiJson } from "@/lib/rest";

const BASE = "/api/billing";
// Administración de cobros: bajo /api/billing/admin (auth-only; los handlers
// componen ManageBilling ∥ org admin), NO bajo el /api/admin de plataforma.
const ADMIN_BASE = `${BASE}/admin`;

const orgSuffix = (org?: string): string =>
  org ? `?${new URLSearchParams({ org }).toString()}` : "";

// ─── Tipos (espejo de los DTO del handler academy_billing.rs) ─────────────────

export interface DebtSummary {
  totalDebtUsdMinor: number;
  openInvoiceCount: number;
  overdueInvoiceCount: number;
  nextDueDate?: string;
  vesRate?: number;
  totalDebtVesMinor?: number;
  organizationId: string;
}

export interface Invoice {
  id: string;
  concept: string;
  seqLabel?: string | null;
  amountUsdMinor: number;
  remainingUsdMinor: number;
  /** Remanente en VES ya convertido por el servidor. El frente NO reconvierte. */
  remainingVesMinor?: number;
  status: "open" | "partial" | "paid" | "void";
  dueDate?: string | null;
  overdue: boolean;
  studentUserId: string;
  // F3-I: nombre/teléfono del alumno para "Cobrar por WhatsApp" (filas vencidas).
  studentName?: string | null;
  studentPhone?: string | null;
}

export interface Payment {
  id: string;
  studentUserId: string;
  methodKey: string;
  currency: "USD" | "VES";
  amountMinor: number;
  amountUsdMinor: number;
  /**
   * Importe en VES para pintar, del servidor. Para un pago hecho EN VES es lo
   * que la academia recibió aquel día, no una reconversión a la tasa de hoy.
   */
  amountVesMinor?: number;
  status: "pending_verification" | "verified" | "rejected";
  referenceNumber?: string | null;
  receiptAssetKey?: string | null;
  createdAt: string;
}

/** B3 · Kill-switch de tres estados de una pasarela para una organización. */
export type GatewayState = "active" | "draining" | "off";

export interface PaymentMethod {
  key: string;
  catalogKey: string;
  name: string;
  kind: "manual" | "online";
  currencies: ("USD" | "VES")[];
  requiresReference: boolean;
  enabled: boolean;
  /**
   * B3 · Quién lo apagó. Sin esto la UI solo puede decir «no disponible», y el
   * alumno no sabe si esperar (la plataforma lo arreglará) o cambiar de método
   * (su academia no lo ofrece).
   */
  disabledBy?: "platform" | "organization";
  state: GatewayState;
  /** `draining` sigue liquidando webhooks pero ya no abre cobros nuevos. */
  acceptsNewCheckout: boolean;
}

/** B3 · Fila del panel de conciliación y drenaje del admin. */
export interface GatewayPanelRow {
  key: string;
  name: string;
  kind: "manual" | "online";
  state: GatewayState;
  offeredByOrg: boolean;
  /** Pagos EN VUELO: lo que aún tiene que aterrizar antes de poder apagarla. */
  inFlightCount: number;
  oldestInFlightAt?: string | null;
  collectedUsdMinor30d: number;
  providerFeeUsdMinor30d: number;
  paymentCount30d: number;
  pendingEvents: { status: string; count: number }[];
  supports: {
    refund: boolean;
    partialRefund: boolean;
    cancel: boolean;
    reconcile: boolean;
    sandbox: boolean;
    feeReported: boolean;
  };
  confirmationModel: string;
}

/** B3 · Evento de la cola de webhooks. */
export interface WebhookEvent {
  id: string;
  provider: string;
  eventId: string;
  providerRef?: string | null;
  status: "received" | "processed" | "failed" | "manual" | "ignored";
  eventStatus?: string | null;
  attempts: number;
  lastError?: string | null;
  receivedAt: string;
  paymentId?: string | null;
}

/** B4 · Presupuesto de un prepago (todo en USD menor; el VES es display). */
export interface PrepayQuote {
  installmentCount: number;
  baseUsdMinor: number;
  discountUsdMinor: number;
  totalUsdMinor: number;
  /** Centésimas de punto: 1050 = 10,50 %. */
  percentBp: number;
}

/** B4 · G3 — la oferta «Adelanta y ahorra» de una matrícula. */
export interface PrepayOffer {
  enrollmentId: string;
  eligibility:
    | "eligible"
    | "first_installment_unpaid"
    | "not_enough_installments"
    | "already_prepaid";
  quote?: PrepayQuote;
  /** Los tres importes de la tarjeta en VES, convertidos por el servidor. */
  baseVesMinor?: number;
  savingsVesMinor?: number;
  totalVesMinor?: number;
  vesRate?: number;
  installmentIds: string[];
}

/** B4 · G5 — cuadro de caja del dueño. */
export interface CashForecast {
  from: string;
  to: string;
  prepaidCollectedUsdMinor: number;
  prepayDiscountUsdMinor: number;
  projectedUsdMinor: number;
  overdueUsdMinor: number;
  expectedUsdMinor: number;
  collectedSharePercent: number;
  vesRate?: number | null;
  /** Cada cifra del cuadro en VES, convertida por el servidor (un solo redondeo). */
  prepaidCollectedVesMinor?: number | null;
  prepayDiscountVesMinor?: number | null;
  projectedVesMinor?: number | null;
  overdueVesMinor?: number | null;
  expectedVesMinor?: number | null;
}

export interface Rate {
  id: string;
  vesPerUsd: number;
  rateDate: string;
  source: "manual" | "bcv_auto";
}

export interface Plan {
  id: string;
  scopeType: string;
  scopeId: string;
  name: string;
  kind: string;
  amountUsdMinor: number;
  installmentCount?: number | null;
  installmentAmountUsdMinor?: number | null;
  enrollmentFeeUsdMinor: number;
  active: boolean;
}

export interface Discount {
  id: string;
  kind: string;
  code?: string | null;
  percent?: number | null;
  amountUsdMinor?: number | null;
  maxUses?: number | null;
  usedCount: number;
  active: boolean;
  // AUTONOMY: ventana de validez gestionable desde la UI.
  validFrom?: string | null;
  validTo?: string | null;
}

export interface AllocationInput {
  invoiceId: string;
  amountUsdMinor: number;
}

export interface RecordPaymentInput {
  org?: string;
  studentUserId: string;
  methodKey: string;
  currency: "USD" | "VES";
  amountMinor: number;
  exchangeRateId?: string;
  referenceNumber?: string;
  receiptAssetKey?: string;
  notes?: string;
  allocations: AllocationInput[];
  /**
   * Id de operación generado por el cliente (uuid por intento de formulario):
   * el servidor lo honra con unicidad por organización — un double-submit o un
   * reintento devuelven el pago YA registrado en vez de duplicarlo.
   */
  clientOperationId?: string;
}

// ─── Alumno ─────────────────────────────────────────────────────────────────

export const useMeSummary = () =>
  createQuery(() => ({
    queryKey: queryKeys.billing.meSummary(),
    queryFn: () => apiJson<DebtSummary>(`${BASE}/me/summary`),
  }));

export const useMeInvoices = () =>
  createQuery(() => ({
    queryKey: queryKeys.billing.meInvoices(),
    queryFn: () => apiJson<Invoice[]>(`${BASE}/me/invoices`),
  }));

export const useMePayments = () =>
  createQuery(() => ({
    queryKey: queryKeys.billing.mePayments(),
    queryFn: () => apiJson<Payment[]>(`${BASE}/me/payments`),
  }));

export const useMethods = (org: () => string | undefined) =>
  createQuery(() => ({
    queryKey: queryKeys.billing.methods(org()),
    queryFn: () => apiJson<PaymentMethod[]>(`${BASE}/methods${orgSuffix(org())}`),
  }));

export const useCheckout = () =>
  createMutation(() => ({
    /**
     * B3 · `methodKey` ausente = `stripe`, que es lo ya desplegado. El kit
     * existe justamente para que la pasarela deje de ser una constante.
     */
    mutationFn: (input: { invoiceIds: string[]; methodKey?: string }) =>
      apiJson<{ checkoutUrl: string; intentId: string }>(`${BASE}/checkout`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
  }));

// ─── B4 · Prepago con descuento (G3) ────────────────────────────────────────

export const useMePrepayOffers = () =>
  createQuery(() => ({
    queryKey: queryKeys.billing.mePrepayOffers(),
    queryFn: () => apiJson<PrepayOffer[]>(`${BASE}/me/prepay-offers`),
  }));

/**
 * Acepta la oferta. Se mandan los DOS importes que el alumno tenía delante: si
 * el estado cambió mientras decidía (alguien le abonó una cuota), el servidor
 * rechaza en vez de cobrarle un importe que no aprobó.
 */
export const useAcceptPrepay = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: {
      enrollmentId: string;
      expectedTotalUsdMinor: number;
      expectedDiscountUsdMinor: number;
    }) =>
      apiJson<{ settlementId: string; invoiceId: string; quote: PrepayQuote }>(
        `${BASE}/me/prepay`,
        {
          method: "POST",
          body: JSON.stringify(input),
        },
      ),
    onSuccess: () => {
      // Aceptar sustituye N cuotas por una: la deuda, las facturas y la propia
      // oferta cambian a la vez.
      void qc.invalidateQueries({ queryKey: queryKeys.billing.mePrepayOffers() });
      void qc.invalidateQueries({ queryKey: queryKeys.billing.meInvoices() });
      void qc.invalidateQueries({ queryKey: queryKeys.billing.meSummary() });
      void qc.invalidateQueries({ queryKey: queryKeys.billing.invoicesAll() });
    },
  }));
};

// ─── Caja / staff ─────────────────────────────────────────────────────────────

export const usePayments = (org: () => string | undefined, status: () => string | undefined) =>
  createQuery(() => ({
    queryKey: queryKeys.billing.payments(org(), status()),
    queryFn: () => {
      const params = new URLSearchParams();
      const o = org();
      const s = status();
      if (o) {
        params.set("org", o);
      }
      if (s) {
        params.set("status", s);
      }
      const qs = params.toString();
      return apiJson<Payment[]>(`${BASE}/payments${qs ? `?${qs}` : ""}`);
    },
  }));

export const useInvoices = (
  org: () => string | undefined,
  student: () => string | undefined,
  status: () => string | undefined,
  overdue: () => boolean,
) =>
  createQuery(() => ({
    queryKey: queryKeys.billing.invoices(org(), student(), status(), overdue()),
    queryFn: () => {
      const params = new URLSearchParams();
      const o = org();
      const st = student();
      const s = status();
      if (o) {
        params.set("org", o);
      }
      if (st) {
        params.set("studentUserId", st);
      }
      if (s) {
        params.set("status", s);
      }
      if (overdue()) {
        params.set("overdue", "true");
      }
      const qs = params.toString();
      return apiJson<Invoice[]>(`${BASE}/invoices${qs ? `?${qs}` : ""}`);
    },
  }));

export const useRates = (org: () => string | undefined) =>
  createQuery(() => ({
    queryKey: queryKeys.billing.rates(org()),
    queryFn: () => apiJson<Rate[]>(`${BASE}/rates${orgSuffix(org())}`),
  }));

export const usePlans = (org: () => string | undefined) =>
  createQuery(() => ({
    queryKey: queryKeys.billing.plans(org()),
    queryFn: () => apiJson<Plan[]>(`${ADMIN_BASE}/plans${orgSuffix(org())}`),
  }));

export const useDiscounts = (org: () => string | undefined) =>
  createQuery(() => ({
    queryKey: queryKeys.billing.discounts(org()),
    queryFn: () => apiJson<Discount[]>(`${ADMIN_BASE}/discounts${orgSuffix(org())}`),
  }));

// ── AUTONOMY: configuración de cobros self-service (admin.billing) ──────────

/** Config de cobros de la org: `{"methods.enabled": string[], "receipt.footer": string}`. */
export const useBillingConfig = (org: () => string | undefined) =>
  createQuery(() => ({
    queryKey: queryKeys.billing.config(org()),
    queryFn: () => apiJson<Record<string, unknown>>(`${ADMIN_BASE}/config${orgSuffix(org())}`),
  }));

export const usePutBillingConfig = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: { org?: string; key: string; value: unknown }) =>
      apiJson<{ ok: boolean }>(`${ADMIN_BASE}/config`, {
        method: "PUT",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.billing.configAll() });
      void qc.invalidateQueries({ queryKey: queryKeys.billing.methodsAll() });
    },
  }));
};

export const useCreatePlan = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: {
      org?: string;
      scopeType: string;
      scopeId: string;
      name: string;
      kind: string;
      amountUsdMinor: number;
      installmentCount?: number;
      installmentAmountUsdMinor?: number;
      enrollmentFeeUsdMinor?: number;
    }) =>
      apiJson<{ id: string }>(`${ADMIN_BASE}/plans`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.billing.plansAll() }),
  }));
};

export const useUpdatePlan = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: { id: string; active?: boolean; name?: string }) =>
      apiJson<{ ok: boolean }>(`${ADMIN_BASE}/plans/${input.id}`, {
        method: "PATCH",
        body: JSON.stringify({ active: input.active, name: input.name }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.billing.plansAll() }),
  }));
};

export const useCreateDiscount = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: {
      org?: string;
      kind: string;
      code?: string;
      percent?: number;
      amountUsdMinor?: number;
      maxUses?: number;
      validFrom?: string;
      validTo?: string;
    }) =>
      apiJson<{ id: string }>(`${ADMIN_BASE}/discounts`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.billing.discountsAll() }),
  }));
};

export const useUpdateDiscount = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: {
      id: string;
      active?: boolean;
      maxUses?: number;
      validFrom?: string;
      validTo?: string;
      clearValidity?: boolean;
    }) =>
      apiJson<{ ok: boolean }>(`${ADMIN_BASE}/discounts/${input.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          active: input.active,
          maxUses: input.maxUses,
          validFrom: input.validFrom,
          validTo: input.validTo,
          clearValidity: input.clearValidity,
        }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.billing.discountsAll() }),
  }));
};

// ── fin AUTONOMY ────────────────────────────────────────────────────────────

export const useRecordPayment = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: RecordPaymentInput) =>
      apiJson<{ id: string }>(`${BASE}/payments`, { method: "POST", body: JSON.stringify(input) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.billing.paymentsAll() });
      void qc.invalidateQueries({ queryKey: queryKeys.billing.invoicesAll() });
    },
  }));
};

export const useAddRate = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: { org?: string; vesPerUsd?: number; rateDate?: string; auto?: boolean }) =>
      apiJson<{ id: string }>(`${BASE}/rates`, { method: "POST", body: JSON.stringify(input) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.billing.ratesAll() }),
  }));
};

export const useCreateInvoice = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: {
      org?: string;
      studentUserId: string;
      concept: string;
      amountUsdMinor: number;
      dueDate?: string;
      enrollmentId?: string;
    }) =>
      apiJson<{ id: string }>(`${BASE}/invoices`, { method: "POST", body: JSON.stringify(input) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.billing.invoicesAll() }),
  }));
};

export const useGenerateInvoices = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: {
      enrollmentId: string;
      org?: string;
      studentUserId: string;
      planId: string;
      startDate?: string;
      discountCode?: string;
    }) =>
      apiJson<{ invoiceIds: string[] }>(
        `${BASE}/enrollments/${encodeURIComponent(input.enrollmentId)}/generate-invoices`,
        { method: "POST", body: JSON.stringify(input) },
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.billing.invoicesAll() }),
  }));
};

// ─── Admin: verificación ──────────────────────────────────────────────────────

export const useVerifyPayment = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (paymentId: string) =>
      apiJson<{ ok: boolean }>(`${ADMIN_BASE}/payments/${encodeURIComponent(paymentId)}/verify`, {
        method: "POST",
        body: "{}",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.billing.paymentsAll() });
      void qc.invalidateQueries({ queryKey: queryKeys.billing.invoicesAll() });
    },
  }));
};

export const useRejectPayment = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: { paymentId: string; reason: string }) =>
      apiJson<{ ok: boolean }>(
        `${ADMIN_BASE}/payments/${encodeURIComponent(input.paymentId)}/reject`,
        {
          method: "POST",
          body: JSON.stringify({ reason: input.reason }),
        },
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.billing.paymentsAll() }),
  }));
};

// ─── B3 · Operación de pasarelas (admin) ────────────────────────────────────

/** Panel de conciliación y drenaje: estado, dinero en vuelo y cola por pasarela. */
export const useGatewayPanel = (org: () => string | undefined) =>
  createQuery(() => ({
    queryKey: queryKeys.billing.gateways(org()),
    queryFn: () => apiJson<GatewayPanelRow[]>(`${ADMIN_BASE}/gateways${orgSuffix(org())}`),
  }));

/**
 * Kill-switch de tres estados. **`draining` es el valor operativo**: corta los
 * cobros nuevos y deja que lo que está en vuelo aterrice. `off` solo tras el
 * drenaje completo o ante un incidente grave — y ni siquiera entonces descarta
 * un webhook: lo persiste para liquidarlo a mano.
 */
export const useSetGatewayState = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: { org?: string; key: string; state: GatewayState }) =>
      apiJson<{ ok: boolean; inFlightCount: number }>(
        `${ADMIN_BASE}/gateways/${encodeURIComponent(input.key)}/state${orgSuffix(input.org)}`,
        { method: "POST", body: JSON.stringify({ state: input.state }) },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.billing.gatewaysAll() });
      void qc.invalidateQueries({ queryKey: queryKeys.billing.methodsAll() });
    },
  }));
};

export const useWebhookEvents = (org: () => string | undefined, status: () => string | undefined) =>
  createQuery(() => ({
    queryKey: queryKeys.billing.webhookEvents(org(), status()),
    queryFn: () => {
      const params = new URLSearchParams();
      const o = org();
      const s = status();
      if (o) {
        params.set("org", o);
      }
      if (s) {
        params.set("status", s);
      }
      const qs = params.toString();
      return apiJson<WebhookEvent[]>(`${ADMIN_BASE}/webhook-events${qs ? `?${qs}` : ""}`);
    },
  }));

/** Liquidación MANUAL de un evento que la cola no pudo aplicar sola (auditada). */
export const useSettleWebhookEvent = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: { org?: string; id: string; note: string }) =>
      apiJson<{ ok: boolean }>(
        `${ADMIN_BASE}/webhook-events/${encodeURIComponent(input.id)}/settle${orgSuffix(input.org)}`,
        { method: "POST", body: JSON.stringify({ note: input.note }) },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.billing.webhookEventsAll() });
      void qc.invalidateQueries({ queryKey: queryKeys.billing.gatewaysAll() });
    },
  }));
};

/** B4 · G5 — cuadro de caja del dueño (cobrado adelantado vs proyectado). */
export const useCashForecast = (
  org: () => string | undefined,
  from: () => string,
  to: () => string,
) =>
  createQuery(() => ({
    queryKey: queryKeys.billing.cashForecast(org(), from(), to()),
    queryFn: () => {
      const params = new URLSearchParams({ from: from(), to: to() });
      const o = org();
      if (o) {
        params.set("org", o);
      }
      return apiJson<CashForecast>(`${ADMIN_BASE}/cash-forecast?${params.toString()}`);
    },
  }));

/** URL del recibo imprimible (HTML autocontenido) de un pago. */
export const receiptUrl = (paymentId: string): string =>
  `${BASE}/receipt/${encodeURIComponent(paymentId)}`;
